import * as lossCurvesSupport from "./LossCurves.support";
import * as support from "./StateSummary.support";
import {
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  SettingsBackupRestore as ResetIcon,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import { DataGrid, GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";
import * as logger from "common/logger";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import { makeChoicesColDef } from "components/common/dataGrid/choicesColumn";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import * as pricingHooks from "components/customPricing/dnfModel1/pricingHooks";
import * as labels from "config.labels";
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as analyticsStore from "store/analytics";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as modelStore from "store/modelDataStore";

const BLANK_ROW_ID = "__BLANK__";

const DISTANCE_BUCKET_DEFINITIONS_V1 = [
  {
    key: "zeroToOneMile",
    label: "0-1 Mile",
  },
  {
    key: "oneToTwoMiles",
    label: "1-2 Miles",
  },
  {
    key: "twoToFiveMiles",
    label: "2-5 Miles",
  },
  {
    key: "fiveToTenMiles",
    label: "5-10 Miles",
  },
  {
    key: "tenToTwentyMiles",
    label: "10-20 Miles",
  },
  {
    key: "twentyPlusMiles",
    label: "20+ Miles",
  },
];

const DISTANCE_BUCKET_DEFINITIONS_V2 = [
  {
    key: "v2.zeroToPointTwoMile",
    label: ">= 0.0 and < 0.2",
  },
  {
    key: "v2.pointTwoToOneMile",
    label: ">= 0.2 and < 1.0",
  },
  {
    key: "v2.oneToTwoPointFiveMile",
    label: ">= 1.0 and < 2.5",
  },
  {
    key: "v2.twoPointFiveToFiveMile",
    label: ">= 2.5 and < 5.0",
  },
  {
    key: "v2.fiveToTenMile",
    label: ">= 5.0 and < 10.0",
  },
  {
    key: "v2.tenToTwentyFiveMile",
    label: ">= 10.0 and < 25.0",
  },
  {
    key: "v2.twentyFiveToOneHundredMile",
    label: ">= 25.0 and < 100.0",
  },
];

const PasteManager = ({ children, onPaste }) => {
  const wrapperRef = useRef(null);
  const lastClickRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (wrapper) {
      const handlePasteEvent = (event) => {
        event.preventDefault();
        if (onPaste && event.clipboardData) {
          onPaste({
            lastClick: lastClickRef.current,
            pastedText: event.clipboardData.getData("text/plain"),
          });
        }
      };
      wrapper.addEventListener("paste", handlePasteEvent);
      return () => {
        wrapper.removeEventListener("paste", handlePasteEvent);
      };
    }
  }, [wrapperRef, lastClickRef, onPaste]);

  return (
    <Box ref={wrapperRef}>
      {Children.map(children, (child) =>
        isValidElement(child)
          ? cloneElement(child, {
              slotProps: {
                cell: {
                  onFocus: (event) => {
                    lastClickRef.current = {
                      rowId: event.currentTarget.parentElement.dataset.id,
                      columnId: event.currentTarget.dataset.field,
                    };
                  },
                },
              },
            })
          : child
      )}
    </Box>
  );
};

const StateSummaryGrid = ({
  gridData,
  distanceBucketDefinitions,
  updateOverrides,
  updateStateOverrides,
  deleteStateOverrides,
  disabled = false,
}) => {
  const apiRef = useGridApiRef();

  const { data: geographyConfig } = staticDataService.useConfigQuery(
    "geography"
  );

  const columns = [
    makeChoicesColDef({
      field: "state",
      headerName: "State",
      choices:
        geographyConfig?.usStates?.map((item) => ({
          key: item.code,
          name: item.name,
        })) ?? [],
      freeSolo: true,
      translations: {
        __UNKNOWN__: "[Unknown]",
      },
      minWidth: 200,
      cellClassName: (params) =>
        params?.row?.isCalculated ? "not-editable" : "",
    }),
    makePrettyNumberColDef({
      field: "tiv",
      headerName: "TIV",
      minWidth: 150,
      prefix: "$",
      cellClassName: (params) =>
        params?.row?.isCalculated && params?.row?.overriddenTiv != null
          ? "overridden"
          : "",
    }),
    ...(distanceBucketDefinitions ?? [])
      .filter((bucket) => !!bucket?.key && !!bucket?.label)
      .map(({ key, label }) =>
        makePrettyNumberColDef({
          field: key,
          headerName: label,
          minWidth: 160,
          prefix: "$",
        })
      ),
    {
      field: "actions",
      type: "actions",
      width: 80,
      getActions: (params) =>
        params?.id != null &&
        params?.row?.id !== BLANK_ROW_ID &&
        !params?.row?.isCalculated
          ? [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={"Delete"}
                onClick={() => {
                  deleteStateOverrides(params?.id);
                }}
              />,
            ]
          : [],
    },
  ].map((column) => (disabled ? { ...column, editable: false } : column));

  const onPaste = ({ lastClick, pastedText }) => {
    if (lastClick?.rowId == null || lastClick?.columnId == null) {
      return;
    }

    const pastedGrid = lossCurvesSupport.parseFloatTable(pastedText);

    const rows = gridData?.map(({ id }) => id) ?? [];
    const rowStartIndex = rows.indexOf(lastClick.rowId);

    const columns = [
      "tiv",
      ...(distanceBucketDefinitions ?? [])
        .filter((bucket) => !!bucket?.key && !!bucket?.label)
        .map(({ key }) => key),
    ];
    const columnStartIndex = columns.indexOf(lastClick.columnId);

    if (rowStartIndex === -1 || columnStartIndex === -1) {
      return;
    }

    const overrides = {};
    (pastedGrid ?? []).forEach((pastedRow, pastedRowIndex) => {
      const rowIndex = rowStartIndex + pastedRowIndex;
      const state = rows[rowIndex];
      if (state && state !== BLANK_ROW_ID) {
        const values = {};
        (pastedRow ?? []).forEach((value, itemColumnIndex) => {
          const columnIndex = columnStartIndex + itemColumnIndex;
          const column = columns[columnIndex];
          if (column) {
            // Only set value if it's not null (preserves blank cells)
            if (value !== null) {
              values[column] = value;
            }
          }
        });
        if (Object.keys(values).length > 0) {
          overrides[state] = values;
        }
      }
    });

    updateOverrides(overrides);
  };

  return (
    <Box
      sx={{
        width: "100%",
        "& .not-editable": {
          bgcolor: "#EFEFEF",
        },
        "& .overridden": {
          color: "white",
          backgroundColor: "primary.main",
          fontWeight: "bold",
        },
      }}
    >
      <PasteManager onPaste={onPaste}>
        <DataGrid
          apiRef={apiRef}
          columns={columns}
          isCellEditable={(params) => {
            if (params?.field === "state") {
              return params?.row?.id === BLANK_ROW_ID;
            } else {
              return true;
            }
          }}
          rows={[...gridData, { id: BLANK_ROW_ID }]}
          rowHeight={25}
          getCellClassName={(_) => "tabular"}
          processRowUpdate={(newRow, oldRow) => {
            const updates = Object.fromEntries(
              Object.entries(newRow ?? {}).filter(
                ([key, value]) => oldRow?.[key] !== value
              )
            );
            updateStateOverrides(newRow.state)(updates);
            return newRow;
          }}
          onProcessRowUpdateError={(error) => logger.error(error)}
          onCellEditStart={(params, event) => {
            if (params?.reason === "deleteKeyDown") {
              updateStateOverrides(params?.id)({
                [params?.field]: null,
              });
              event.defaultMuiPrevented = true;
              return;
            } else if (params?.reason === "pasteKeyDown") {
              event.defaultMuiPrevented = true;
              return;
            }
          }}
          hideFooter
          disableColumnMenu
          autoHeight
        />
      </PasteManager>
    </Box>
  );
};

const ActionMenu = ({ setDistanceBucketDefinitions }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");

  const anchorRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={"menu"}>
      <IconButton
        ref={anchorRef}
        onClick={() => setIsOpen(!isOpen)}
        variant={"text"}
        className={"toggle"}
        style={{ marginLeft: "-15px" }}
      >
        <MoreIcon />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={(_, reason) => {
          if (reason === "backdropClick") setIsOpen(!isOpen);
        }}
      >
        <MenuItem
          onClick={() => {
            setDistanceBucketDefinitions(DISTANCE_BUCKET_DEFINITIONS_V2);
            setIsOpen(false);
          }}
        >
          {" "}
          <ListItemIcon>
            <ResetIcon />
          </ListItemIcon>
          <ListItemText>
            {translateLabels(labels.STATE_SUMMARY_RESET_TO_V2_BUCKETS, {
              default: "Reset using latest buckets (RMS 2025-03)",
            })}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDistanceBucketDefinitions(DISTANCE_BUCKET_DEFINITIONS_V1);
            setIsOpen(false);
          }}
        >
          <ListItemIcon>
            <ResetIcon />
          </ListItemIcon>
          <ListItemText>
            {translateLabels(labels.STATE_SUMMARY_RESET_TO_V1_BUCKETS, {
              default: "Reset using V1 buckets (RMS 2024)",
            })}
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

const StateSummary = () => {
  const dispatch = useDispatch();
  const modelData = useSelector(modelStore.selectModelData);
  const overrides = modelData?.stateSummaryOverrides ?? {};

  pricingHooks.usePricing();

  const stateTiv =
    useSelector(analyticsStore.select)?.programPricing?.values?.geoSummary
      ?.stateTiv ?? {};

  const updateOverrides = (values) => {
    // Move to a separate render to make sure that the table updates correctly.
    setTimeout(
      () =>
        dispatch(
          modelStore.update({
            ...modelData,
            stateSummaryOverrides: {
              ...overrides,
              ...Object.fromEntries(
                Object.entries(values ?? {}).map(([id, stateValues]) => [
                  id,
                  {
                    ...overrides?.[id],
                    ...stateValues,
                  },
                ])
              ),
            },
          })
        ),
      0
    );
  };

  const updateStateOverrides = (state) => (values) =>
    updateOverrides({
      [state]: values,
    });

  const deleteStateOverrides = (state) => {
    dispatch(
      modelStore.update({
        ...modelData,
        stateSummaryOverrides: Object.fromEntries(
          Object.entries(overrides ?? {}).filter(
            ([thisState, _]) => thisState !== state
          )
        ),
      })
    );
  };

  const setDistanceBucketDefinitions = (distanceBucketDefinitions) => {
    dispatch(
      modelStore.update({
        ...modelData,
        distanceBucketDefinitions,
        stateSummaryOverrides: {},
      })
    );
  };

  const gridData = support.buildGridData({
    stateTiv: modelData?.stateSummaryIgnoreCalculated ? {} : stateTiv,
    overrides,
  });

  return (
    <Component
      title={"State Summary"}
      options={
        <ActionMenu
          setDistanceBucketDefinitions={setDistanceBucketDefinitions}
        />
      }
    >
      <Stack direction={"column"} spacing={2}>
        <Disabled ifReadOnly>
          <StateSummaryGrid
            gridData={gridData}
            distanceBucketDefinitions={
              modelData?.distanceBucketDefinitions ??
              DISTANCE_BUCKET_DEFINITIONS_V1
            }
            updateOverrides={updateOverrides}
            updateStateOverrides={updateStateOverrides}
            deleteStateOverrides={deleteStateOverrides}
          />
        </Disabled>
      </Stack>
    </Component>
  );
};

export default StateSummary;
